// One duration formatter, three styles (TS-03).
//
// Seven formatters grew in four libs and two components — fmtClock (m:ss under an
// hour), timer.tsx's HH:MM:SS, fmtHM (h:mm), fmtHms (h:mm:ss), dayClockLog's fmtDur
// ("1h 05m"), fmtHours ("2.5h") and bare toFixed(1) — so "0:10" meant ten seconds
// in one row and "2:00" two hours in the next, with nothing to say which. Each
// style below has ONE shape, and no two styles share a shape:
//
//   'clock'   → "0:04:12"  always H:MM:SS, hours never dropped — a live or exact figure
//   'hm'      → "7h 30m"   units spelled out — a span a person reads ("away 40m")
//   'decimal' → "7.5"      hours to one decimal — a figure that is summed (grids, KPIs)
//   'perDay'  → "8h / day" a contracted or standard day, the rate a person works to;
//               "7h 30m / day" when it isn't whole hours
//
// Hours are never rolled into days: a 25-hour figure is "25:00:00", because every
// consumer is a working-hours total and "1d 1h" would read as a calendar span.
//
// Negative and non-finite input CLAMPS to zero rather than throwing or printing a
// minus: a duration is elapsed time, and a negative one is a clock-skew artefact
// (an end stamped before its start), not a figure anyone should see. A signed
// CHANGE in hours is a different thing — timeCorrections.ts fmtDelta owns that.
//
// Not migrated here: contract.ts fmtHms stays the CSV column's format (it matches
// the org's existing sheet), and the screens move onto this in Phase 3.
// See docs/UI-CONVENTIONS.md §2, "Durations".

export type DurationStyle = 'clock' | 'hm' | 'decimal' | 'perDay';

const pad2 = (n: number) => String(n).padStart(2, '0');

export const fmtDuration = (seconds: number, style: DurationStyle): string => {
  // `> 0` is false for NaN too, so one test clamps both.
  const s = seconds > 0 && Number.isFinite(seconds) ? seconds : 0;
  if (style === 'decimal') return String(Math.round(s / 360) / 10);
  // A contract is set, not measured, so it rounds to the minute: 8 h stored as
  // 7.9999… must not read "7h 59m". No "00m" on a whole day.
  if (style === 'perDay') {
    const min = Math.round(s / 60);
    const [h, m] = [Math.floor(min / 60), min % 60];
    return `${[h && `${h}h`, m && `${h ? pad2(m) : m}m`].filter(Boolean).join(' ') || '0h'} / day`;
  }
  // Floored, like a stopwatch: never show a second or minute that hasn't elapsed.
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (style === 'clock') return `${h}:${pad2(m)}:${pad2(Math.floor(s % 60))}`;
  // "<1m", not "0m": a 30-second pause happened, and zero says it didn't.
  if (h === 0) return s > 0 && m === 0 ? '<1m' : `${m}m`;
  return `${h}h ${pad2(m)}m`;
};
